/**
 * Modelos de calidad y su ciclo de vida.
 *
 * El modelo es la PLANTILLA GENERAL: no lleva área ni ámbito. Su estado gobierna
 * qué se puede editar en toda la aplicación.
 *
 * Nota sobre `getModelo()`: conserva en memoria los modelos recién creados que el
 * API todavía no devuelve, para que no desaparezcan de la lista entre el alta y
 * la primera recarga.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type EstructuraModelo from '@/interfaces/EstructuraModelo'
import {getModelosService, getModeloByIdService, crearModeloService, ActualizarModeloService, eliminarModeloService, cambiarEstadoModeloService, clonarModeloService, descontinuarModeloService, getModeloGraphService} from '@/services/ModelosService'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useComponentes } from '@/store/GestionComponentes'
import { limpiarNiveles } from '@/utils/nivelesHelper'

// --- Helpers ---
function normalizeNiveles(raw: unknown): string[] {
  return limpiarNiveles(raw)
}

function sanitizeNombre(nombre: string): string {
  return (nombre ?? '').trim()
}

function sanitizeNiveles(niveles?: string[] | string | null): string[] {
  if (!niveles) return []
  return limpiarNiveles(niveles)
}

function isValidModeloPayload(m: Omit<EstructuraModelo, 'id_modelo'>): boolean {
  return !!sanitizeNombre(m.nombre) && !!m.fecha_creacion
}

/** Store `Modelos`. Ver la cabecera del archivo para el contrato del módulo. */
export const useModelo = defineStore('Modelos', {
  state: () => ({
    modelos: [] as EstructuraModelo[],
    modeloActual: null as EstructuraModelo | null,
    urlModelos: `${process.env.VUE_APP_API_BASE_URL}/api/modelos`
  }),

  actions: {
    async getModelo() {
      const lista = await getModelosService(this.urlModelos)
      const fromApi = lista.map(m => ({
        ...m,
        nombre: sanitizeNombre(m.nombre),
        niveles: normalizeNiveles(m.niveles)
      }))
      // Keep any in-memory models (e.g. just created without versions) not returned by the API
      const apiIds = new Set(fromApi.map(m => m.id_modelo))
      const extra = this.modelos.filter(m => m.id_modelo && !apiIds.has(m.id_modelo))
      this.modelos = [...fromApi, ...extra]
      return this.modelos
    },

    async getModeloById(id_modelo: number) {
      const modelo = await getModeloByIdService(this.urlModelos, id_modelo)
      if (!modelo) return null
      this.modeloActual = {
        ...modelo,
        nombre: sanitizeNombre(modelo.nombre),
        niveles: normalizeNiveles(modelo.niveles)
      }
      return this.modeloActual
    },

    // Crear modelo + componente raíz (sin versión) — flujo normal no histórico
    // El backend crea la raíz automáticamente en ModeloController::store()
    async crearModeloConRaiz(
      modelo: Omit<EstructuraModelo, 'id_modelo'>
    ) {
      const ok = await this.crearModelo(modelo)
      if (!ok || !this.modeloActual?.id_modelo) {
        return { ok: false, modelo: null, componenteRaizCreado: null }
      }
      const componenteRaiz = this.modeloActual.componente_raiz ?? null
      return { ok: true, modelo: this.modeloActual, componenteRaizCreado: componenteRaiz }
    },

    // Crear modelo, versión inicial y componente raíz — flujo histórico
    // versionOpciones propaga ambito/id_programa a la versión inicial (C-03)
    async crearModeloConVersionInicial(
      modelo: Omit<EstructuraModelo, 'id_modelo'>,
      versionOpciones?: { ambito?: 'institucional' | 'programa' | null; id_programa?: number | null }
    ) {
      const ok = await this.crearModelo(modelo)
      if (!ok || !this.modeloActual?.id_modelo) {
        return { ok: false, modelo: null, versionCreada: null, componenteRaizCreado: null }
      }

      const versionesStore = useGestionVersiones()
      const versionCreada = await versionesStore.crearVersion({
        id_modelo: this.modeloActual.id_modelo,
        nombre: this.modeloActual.nombre,
        estado: this.modeloActual.estado,
        fecha_creacion: this.modeloActual.fecha_creacion,
        fecha_aprobacion: null,
        ...(versionOpciones ?? {})
      })

      if (!versionCreada?.id_version) {
        return { ok: true, modelo: this.modeloActual, versionCreada: null, componenteRaizCreado: null }
      }

      const componentesStore = useComponentes()
      const componenteRaiz = await componentesStore.agregarRaiz({
        id_modelo: this.modeloActual.id_modelo,
        nombre: 'Componente raíz',
        peso: null,
        descripcion: null
      })

      if (!componenteRaiz?.id_componente) {
        console.warn('No se pudo crear el componente raíz para el modelo', this.modeloActual.id_modelo)
        return { ok: false, modelo: this.modeloActual, versionCreada, componenteRaizCreado: null }
      }

      return { ok: true, modelo: this.modeloActual, versionCreada, componenteRaizCreado: componenteRaiz }
    },

    // Crear modelo
    async crearModelo(modelo: Omit<EstructuraModelo, 'id_modelo'>) {
      const payload: Omit<EstructuraModelo, 'id_modelo'> = {
        ...modelo,
        nombre: sanitizeNombre(modelo.nombre),
        niveles: sanitizeNiveles(modelo.niveles)
      }
      if (!isValidModeloPayload(payload)) return false

      const creado = await crearModeloService(this.urlModelos, payload)
      if (!creado) return false

      const normalizado: EstructuraModelo = {
        ...creado,
        nombre: sanitizeNombre(creado.nombre),
        niveles: normalizeNiveles(creado.niveles)
      }
      this.modelos.push(normalizado)
      this.modeloActual = normalizado
      return true
    },

    async updateModelo(modelo: EstructuraModelo) {
      if (!modelo.id_modelo) return false

      const payload: Partial<EstructuraModelo> = {
        nombre: sanitizeNombre(modelo.nombre),
        niveles: sanitizeNiveles(modelo.niveles),
        id_tipo_modelo: modelo.id_tipo_modelo,
        fecha_creacion: modelo.fecha_creacion,
        fecha_descontinuado: modelo.fecha_descontinuado,
        escala_max: modelo.escala_max,
        vigencia_inicio: modelo.vigencia_inicio ?? null,
        vigencia_fin: modelo.vigencia_fin ?? null,
        acuerdo: modelo.acuerdo ?? null
      }

      const actualizado = await ActualizarModeloService(this.urlModelos, modelo.id_modelo, payload)
      if (!actualizado) return false

      const normalizado = {
        ...actualizado,
        nombre: sanitizeNombre(actualizado.nombre),
        niveles: normalizeNiveles(actualizado.niveles)
      }
      const idx = this.modelos.findIndex(m => m.id_modelo === normalizado.id_modelo)
      if (idx !== -1) this.modelos[idx] = normalizado

      if (this.modeloActual?.id_modelo === normalizado.id_modelo) {
        this.modeloActual = normalizado
      }
      return true
    },

    async eliminarModelo(id_modelo: number): Promise<boolean> {
      const ok = await eliminarModeloService(this.urlModelos, id_modelo)
      if (!ok) return false
      this.modelos = this.modelos.filter(m => m.id_modelo !== id_modelo)
      return true
    },

    async cambiarEstadoModelo(id_modelo: number, nuevoEstado: EstructuraModelo['estado']): Promise<boolean> {
      const actualizado = await cambiarEstadoModeloService(this.urlModelos, id_modelo, nuevoEstado)
      if (!actualizado) return false

      const idx = this.modelos.findIndex(m => m.id_modelo === id_modelo)
      if (idx !== -1) this.modelos[idx] = actualizado

      if (this.modeloActual?.id_modelo === id_modelo) {
        this.modeloActual = actualizado
      }
      return true
    },

    // Descontinuar modelo
    async descontinuarModelo(id_modelo: number): Promise<{ ok: boolean; message?: string }> {
      const result = await descontinuarModeloService(this.urlModelos, id_modelo)
      if (result.ok && result.data) {
        const idx = this.modelos.findIndex(m => m.id_modelo === id_modelo)
        if (idx !== -1) this.modelos[idx] = result.data
        if (this.modeloActual?.id_modelo === id_modelo) this.modeloActual = result.data
      }
      return { ok: result.ok, message: result.message }
    },

    // Clonar modelo
    async clonarModelo(
      id_modelo: number,
      opciones?: { ordinales?: string[]; excluir_ordinales?: string[]; nuevo_nombre?: string }
    ): Promise<EstructuraModelo | null> {
      const clonado = await clonarModeloService(this.urlModelos, id_modelo, opciones)
      if (!clonado) return null
      this.modelos.push(clonado)
      this.modeloActual = clonado
      return clonado
    },

    async getModeloGraph(id_modelo: number): Promise<{ nodes: any[]; edges: any[] } | null> {
      return await getModeloGraphService(this.urlModelos, id_modelo)
    }
  }
})

export default useModelo
